import React, { useRef, useEffect, useState } from 'react';
import * as styles from './AWAboutSection.module.css';
import * as generalStyles from './AWGeneral.module.css';
import { useLanguage } from '../../../i18n';

const HISTORY_DATA = [
    {
        bg: "/assets/backgounds/001_bg_absc.png",
        bgMobile: "/assets/backgounds/001_bg_rs_absc.png",
        year: "+70",
        label: "AÑOS",
        subLabel: "MANTELERÍA",
        desc: "Desde 1954, hemos vestido las mesas más exigentes con manteles, servilletas y accesorios de alta gama. Una tradición textil que combina artesanía con innovación."
    },
    {
        bg: "/assets/backgounds/002_bg_absc.png",
        bgMobile: "/assets/backgounds/002_bg_rs_absc.png",
        year: "+18",
        label: "AÑOS",
        subLabel: "UNIFORMES",
        desc: "Diseñamos y confeccionamos uniformes corporativos que proyectan identidad, profesionalismo y funcionalidad. Cada prenda refleja la esencia de tu marca."
    },
    {
        bg: "/assets/backgounds/003_bg_absc.png",
        bgMobile: "/assets/backgounds/003_bg_rs_absc.png",
        year: "+60",
        label: "AÑOS",
        subLabel: "LENCERÍA HOTELES",
        desc: "Proveemos sábanas, toallas y amenidades para la industria hotelera. Confort y durabilidad que superan las expectativas de los huéspedes más exigentes."
    },
    {
        bg: "/assets/backgounds/004_bg_absc.png",
        bgMobile: "/assets/backgounds/004_bg_rs_absc.png",
        year: "+10",
        label: "AÑOS",
        subLabel: "EXPORTANDO",
        desc: "Una década llevando la excelencia venezolana al Caribe y más allá. Aruba, Curazao, y nuevos horizontes que consolidan nuestra presencia internacional."
    }
];

export const AWAboutSection: React.FC = () => {
    const { t } = useLanguage();
    const containerRef = useRef<HTMLDivElement>(null);
    const [scrollProgress, setScrollProgress] = useState(0);

    // Carousel State
    const [currentIndex, setCurrentIndex] = useState(0);
    const [displayedIndex, setDisplayedIndex] = useState(0);
    const [animationState, setAnimationState] = useState<'idle' | 'exiting' | 'entering'>('idle');
    const [progressFill, setProgressFill] = useState(0);

    // Handle slide change with exit-then-enter animation
    const changeSlide = (newIndex: number) => {
        if (newIndex === currentIndex || animationState !== 'idle') return;

        // Start exit animation
        setAnimationState('exiting');

        // After exit animation, change content and enter
        setTimeout(() => {
            setCurrentIndex(newIndex);
            setDisplayedIndex(newIndex);
            setAnimationState('entering');
            setProgressFill(0);

            // After enter animation, go back to idle
            setTimeout(() => {
                setAnimationState('idle');
            }, 400);
        }, 400);
    };

    // Carousel Timer Effect
    useEffect(() => {
        const intervalDuration = 10000; // 10 seconds per slide
        const frameRate = 30;
        let startTime = Date.now();

        const timer = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const p = Math.min(100, (elapsed / intervalDuration) * 100);
            setProgressFill(p);

            if (elapsed >= intervalDuration) {
                const nextIndex = (currentIndex + 1) % HISTORY_DATA.length;
                changeSlide(nextIndex);
                startTime = Date.now();
            }
        }, frameRate);

        return () => clearInterval(timer);
    }, [currentIndex, animationState]);

    // Simple scroll progress - identical to AWClientsSection
    useEffect(() => {
        const handleScroll = () => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const containerTop = rect.top + window.scrollY;
            const containerHeight = containerRef.current.offsetHeight;
            const viewportHeight = window.innerHeight;
            const scrollY = window.scrollY;

            const start = containerTop;
            const end = containerTop + containerHeight - viewportHeight;
            const progress = (scrollY - start) / (end - start);
            setScrollProgress(Math.max(0, Math.min(1, progress)));
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Content appears early (at 2% scroll) and fades out at 85%
    // Smoother fade logic
    let contentOpacity = 0;
    if (scrollProgress < 0.15) {
        contentOpacity = scrollProgress / 0.15;
    } else if (scrollProgress > 0.85) {
        contentOpacity = 1 - (scrollProgress - 0.85) / 0.15; // Start fade earlier (0.85)
    } else {
        contentOpacity = 1;
    }

    // Always render to prevent "pop" unmounting
    const showCenterBlock = true;

    const scrollLineTop = Math.min(100, Math.max(0, scrollProgress * 100)) + '%';

    // Gradual White Overlay instead of abrupt ON/OFF
    const overlayOpacity = scrollProgress > 0.90
        ? (scrollProgress - 0.90) / 0.10
        : 0;

    const currentItem = HISTORY_DATA[displayedIndex];

    // Determine animation class based on state
    const getTextAnimationClass = () => {
        if (animationState === 'exiting') return styles.textFadeOut;
        if (animationState === 'entering') return styles.textFadeIn;
        return '';
    };

    return (
        <div ref={containerRef} className={styles.aboutScrollContainer}>
            <div className={styles.stickyViewport}>
                {/* White Background Transition (Gradual) */}
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: '#fff',
                        opacity: overlayOpacity,
                        transition: 'opacity 0.2s ease-out',
                        pointerEvents: 'none',
                        zIndex: 1
                    }}
                />

                {/* ============================================ */}
                {/* PHASE 1: HISTORY CAROUSEL (50/50 Alternating Layout) */}
                {/* ============================================ */}
                {showCenterBlock && (
                    <div className={styles.historySection} style={{ opacity: contentOpacity, transition: 'opacity 0.4s ease-out' }}>
                        {/* DESKTOP: BACKGROUND IMAGE (Full bleed, positioned opposite to text) */}
                        <div className={styles.bgCarousel}>
                            {HISTORY_DATA.map((item, index) => (
                                <div
                                    key={index}
                                    className={`${styles.bgSlide} ${index === currentIndex ? styles.active : ''}`}
                                    style={{
                                        backgroundImage: `url('${item.bg}')`,
                                        backgroundPosition: index % 2 === 0 ? 'right center' : 'left center'
                                    }}
                                />
                            ))}
                        </div>

                        {/* MOBILE: IMAGE ON TOP + GRADIENT + CONTENT BELOW */}
                        <div className={styles.mobileLayout}>
                            {/* Mobile Image */}
                            <div className={styles.mobileImageContainer}>
                                {HISTORY_DATA.map((item, index) => (
                                    <img
                                        key={index}
                                        src={item.bgMobile}
                                        alt=""
                                        className={`${styles.mobileImage} ${index === displayedIndex ? styles.active : ''}`}
                                    />
                                ))}
                            </div>

                            {/* Gradient overlay that blends with white */}
                            <div className={styles.mobileGradient} />

                            {/* Mobile Content */}
                            <div className={styles.mobileContent}>
                                <div className={`${styles.textContent} ${getTextAnimationClass()}`}>
                                    <div className={styles.sectionLabel}>
                                        SOBRE NOSOTROS
                                    </div>
                                    <div className={styles.bigYearNumber}>
                                        {currentItem.year}
                                    </div>
                                    <div className={styles.yearLabel}>
                                        {currentItem.label} <br />
                                        <span style={{ color: '#000', fontSize: '1.5rem' }}>{currentItem.subLabel}</span>
                                    </div>
                                    <div className={styles.yearDesc}>
                                        {currentItem.desc}
                                    </div>
                                </div>

                                {/* Mobile Progress Bars */}
                                <div className={styles.mobileProgressContainer}>
                                    {HISTORY_DATA.map((_, index) => (
                                        <div
                                            key={index}
                                            className={styles.progressDot}
                                            onClick={() => changeSlide(index)}
                                        >
                                            <div
                                                className={styles.progressFill}
                                                style={{
                                                    width: index === displayedIndex ? `${progressFill}%` : (index < displayedIndex ? '100%' : '0%')
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* DESKTOP: 50/50 LAYOUT (Alternates based on displayedIndex) */}
                        <div className={`${styles.historyLayout} ${displayedIndex % 2 === 1 ? styles.reversed : ''}`}>
                            {/* TEXT SIDE */}
                            <div className={styles.historyTextSide}>
                                <div className={`${styles.textContent} ${getTextAnimationClass()}`}>
                                    <div className={styles.sectionLabel}>
                                        SOBRE NOSOTROS
                                    </div>
                                    <div className={styles.bigYearNumber}>
                                        {currentItem.year}
                                    </div>
                                    <div className={styles.yearLabel}>
                                        {currentItem.label} <br />
                                        <span style={{ color: '#000', fontSize: '1.5rem' }}>{currentItem.subLabel}</span>
                                    </div>
                                    <div className={styles.yearDesc}>
                                        {currentItem.desc}
                                    </div>
                                </div>
                            </div>

                            {/* IMAGE SIDE (Empty - BG shows through) */}
                            <div className={styles.historyImageSide} />
                        </div>

                        {/* PROGRESS BARS */}
                        <div className={styles.progressContainer}>
                            {HISTORY_DATA.map((_, index) => (
                                <div
                                    key={index}
                                    className={styles.progressDot}
                                    onClick={() => changeSlide(index)}
                                >
                                    <div
                                        className={styles.progressFill}
                                        style={{
                                            width: index === currentIndex ? `${progressFill}%` : (index < currentIndex ? '100%' : '0%')
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}


                <div className={`${generalStyles.scrollProgressLine} ${generalStyles.light}`}>
                    <div className={generalStyles.scrollProgressLabel}>PROGRESS</div>
                    <div
                        className={generalStyles.scrollProgressIndicator}
                        style={{ top: scrollLineTop }}
                    />
                </div>

            </div>
        </div>
    );
};
